import events from './event.js';
import _ from './translate.js';
import Logger from './logger.js';
import { textString } from './texts.js';
import TextParser from './textParser.js';

/**
 * En verktygslåda för att komplettera CropImageViewport med kontroller för
 * zoom (via scrollbar) och panorering (via knappar).
 */
export default class ZoomPanToolbox {
    /**
     * @param {CropImageViewport} viewportInstance - Instansen av CropImageViewport som ska styras.
     */
    constructor(viewportInstance, contentWrapper = null) {
        if (!viewportInstance) {
            throw new Error('CropImageViewport instance is required for ZoomPanToolbox.');
        }
        this.viewport = viewportInstance;
        this.wrapper = null; // Den yttre wrappern för verktygslådan
        this.panStepPercent = 0.05; // Standard steg för knappar (5% av synlig yta)
        this.zoomStepPercent = 0.1; // Standard steg för zoom (10%)

        this.contentWrapper = contentWrapper;

        this.init();
    }

    init() {
        this.createWrapper();
        this.createControls();
        this.attachEvents();
    }

    /**
     * Skapar den yttre behållaren för verktygslådan.
     */
    createWrapper() {
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'mscp-zoompan-toolbox-wrapper';
        if (this.contentWrapper) {
            this.contentWrapper.appendChild(this.wrapper);
        }
        else {
            Logger.debug('ZoomPanToolbox: No content wrapper provided, toolbox not added to DOM.');
        }
    }

    /**
     * Skapar alla kontroller (knappar och scrollbar).
     */
    createControls() {
        // --- 1. Horizontal Scrollbar (Zoom) ---
        const scrollContainer = document.createElement('div');
        scrollContainer.className = 'mscp-zoom-control';

        // Etiketter för klarhet
        const zoomOutLabel = document.createElement('span');
        zoomOutLabel.textContent = '-';
        zoomOutLabel.className = 'mscp-zoom-label zoom-out-label';
        zoomOutLabel.addEventListener('click', () => {
            const vp = this.viewport;
            const newScale = Math.max(vp.minScale, vp.scale - (this.zoomStepPercent * vp.maxScale));
            vp.doZoom(newScale);
        });

        const zoomInLabel = document.createElement('span');
        zoomInLabel.textContent = '+';
        zoomInLabel.className = 'mscp-zoom-label zoom-in-label';
        zoomInLabel.addEventListener('click', () => {
            const vp = this.viewport;
            const newScale = Math.min(vp.maxScale, vp.scale + (this.zoomStepPercent * vp.maxScale));
            vp.doZoom(newScale);
        });

        this.horizontalRange = document.createElement('input');
        this.horizontalRange.type = 'range';
        this.horizontalRange.value = 0;
        this.horizontalRange.step = 0.001;
        this.horizontalRange.className = 'mscp-zoom-range';
        this.horizontalRange.title = _('Image Zoom');

        this.horizontalRange.addEventListener('input', () => this.handleScrollbarZoom());

        scrollContainer.appendChild(zoomOutLabel);
        scrollContainer.appendChild(this.horizontalRange);
        scrollContainer.appendChild(zoomInLabel);
        this.wrapper.appendChild(scrollContainer);


        // --- 2. Pan Buttons Container (Vänster, Höger, Upp, Ner på en rad) ---
        const btnContainer = document.createElement('div');
        btnContainer.className = 'mscp-toolbox-buttons mscp-pan-row';

        // Knappdata: [className, text/icon (Pil), action, title]
        // Riktningar anpassade till CropImageViewport's handlePan-logik:
        const panButtonData = [
            // - 'pan-left' (fysiskt vänster): Ökar vp.pos.x -> positiv percent
            ['pan-left', 'left', () => this.handlePan(this.panStepPercent, 'x'), _('Move Left')],
            // - 'pan-right' (fysiskt höger): Minskar vp.pos.x -> negativ percent
            ['pan-right', 'right', () => this.handlePan(-this.panStepPercent, 'x'), _('Move Right')],
            // - 'pan-up' (fysiskt uppåt): Minskar vp.pos.y -> negativ percent
            ['pan-up', 'up', () => this.handlePan(this.panStepPercent, 'y'), _('Move Up')],
            // - 'pan-down' (fysiskt nedåt): Ökar vp.pos.y -> positiv percent
            ['pan-down', 'down', () => this.handlePan(-this.panStepPercent, 'y'), _('Move Down')],
        ];

        panButtonData.forEach(([className, innerHTML, handler, title]) => {
            const button = document.createElement('span');
            button.className = `mscp-control-btn ${className}`;
            button.title = title;
            button.innerHTML = innerHTML;
            button.addEventListener('click', handler);
            btnContainer.appendChild(button);
        });

        this.wrapper.appendChild(btnContainer);

        // create stars container, showing 5 stars
        this.starsContainer = document.createElement('div');
        this.starsContainer.classList.add('mscp-stars-container', 'mscp-resolution-stars');
        this.starsContainer.dataset.grade = '0';
        this.starsContainer.innerHTML = '☆'.repeat(5);
        this.wrapper.appendChild(this.starsContainer);

        // show descriptive text
        this.descriptionText = document.createElement('div');
        this.descriptionText.className = 'mscp-stars-text';
        this.descriptionText.textContent = '';
        this.wrapper.appendChild(this.descriptionText);

        // Add general info text
        const generalInfoText = document.createElement('div');
        generalInfoText.className = 'mscp-adjust-general-info';
        generalInfoText.innerHTML = textString('adjust_general_info');
        this.wrapper.appendChild(generalInfoText);

    }

    /**
     * Kopplar lyssnare för att uppdatera scrollbaren när viewporten ändras.
     */
    attachEvents() {
        // Lyssna på att viewportens position/skala uppdaterats
        events.add('cropBoxUpdated', () => this.updateControls());
        events.add('cropSizeUpdated', () => this.updateControls());

        // Lyssna på att justeringsläge aktiveras/avaktiveras
        events.add('preAdjustModeToggled', (toggle) => {
            if (toggle) {
                this.wrapper.style.display = 'flex'; // Antar att flex används för layout
            } else {
                this.wrapper.style.display = 'none';
            }
        });

        events.add('currentResolutionGradeUpdated', (grade) => {
            if (!this.starsContainer) return;
            this.starsContainer.dataset.grade = grade; // Set the grade as a data attribute
            this.starsContainer.innerHTML = '★'.repeat(grade) + '☆'.repeat(5 - grade);
            this.descriptionText.innerHTML = textString(`resolution_label_${grade}`);
            TextParser.link(this.descriptionText, (attrs) => {
                if (grade <= 0) return;
                const prop_name = `resolution_info_${grade}`;
                events.trigger('openAdditionalInfoPopup', {
                    text_prop_name: prop_name
                });
            });
        });
        this.updateControls();
    }

    /**
     * Uppdaterar scrollbarens min/max och värde baserat på aktuell zoom-skala.
     */
    updateControls() {
        const vp = this.viewport;
        if (!vp.img || vp.img.naturalWidth === 0) {
            this.horizontalRange.min = 0;
            this.horizontalRange.max = 1;
            this.horizontalRange.value = 0;
            return;
        }

        // Sätt min/max baserat på CropImageViewports gränser
        this.horizontalRange.min = vp.minScale;
        this.horizontalRange.max = vp.maxScale;

        // Sätt scrollbarens värde till aktuell skala
        this.horizontalRange.value = vp.scale;
    }

    /**
     * Hanterar panoreringsknappar (Upp, Ner, Höger, Vänster).
     * @param {number} percent - Andel av den aktuella synliga bredden/höjden att flytta.
     * @param {'x'|'y'} axis - Vilken axel som ska flyttas.
     */
    handlePan(percent, axis) {
        if (!this.viewport.checkIfInAdjustMode()) return;

        const vp = this.viewport;
        const containerRect = vp.cropcontainer.getBoundingClientRect();
        let movement = 0;

        if (axis === 'x') {
            movement = containerRect.width * percent;
            vp.pos.x += movement;
        } else if (axis === 'y') {
            movement = containerRect.height * percent;
            vp.pos.y += movement;
        }

        vp.applyCropBox();
        this.updateControls();
    }

    /**
     * Hanterar ändring från den horisontella scrollbaren (Zoom).
     */
    handleScrollbarZoom() {
        if (!this.viewport.checkIfInAdjustMode()) return;

        const vp = this.viewport;
        const newScale = parseFloat(this.horizontalRange.value);

        if (Math.abs(newScale - vp.scale) < this.horizontalRange.step) {
            return;
        }

        // Do the actual zoom
        vp.doZoom(newScale);
    }

    /**
     * Hämtar den yttre wrappern för placering i DOM.
     * @returns {HTMLElement}
     */
    getWrapper() {
        return this.wrapper;
    }
}